import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_ID, NgModule } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { Meta, Title } from '@angular/platform-browser';
import { AuthModule } from '@app/auth/auth.module';
import { Store } from '@app/store';
import { CoreModule } from '@core/core.module';
import { AuthInterceptor, ContentLanguageInterceptor, ConvertToCamelCase, ConvertToSnakeCase } from '@core/interceptors';
import { PerfTimingInterceptor } from '@core/interceptors/perf-timing.interceptor';
import { GoogleService } from '@core/services/google/google.service';
import { PageService } from '@core/services/page/page.service';
import { CreatorModule } from '@modules/creator/creator.module';
import { CursorsWrapperComponent } from '@modules/cursors/components/cursors-wrapper/cursors-wrapper.component';
import { DisableNavWhenEdit } from '@modules/editor/services/disable-nav-when-edit.service';
import { LoadersWrapperComponent } from '@modules/loaders/components/loaders-wrapper/loaders-wrapper.component';
import { LoaderInterceptor } from '@modules/loaders/interceptors/loader.interceptor';
import { MainModule } from '@modules/main/main.module';
import { MaterialModule } from '@modules/material/material.module';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MatPaginatorIntlPL } from '@shared/helpers/mat-paginator-intl-pl';
import { LocalStorageService, SessionStorageService } from '@shared/services/storage';
import { SharedModule } from '@shared/shared.module';
import { QuillModule } from 'ngx-quill';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    SharedModule.forRoot(),
    AuthModule.fromRoot(),
    AppRoutingModule,
    HttpClientModule,
    CoreModule,
    MaterialModule,
    MainModule,
    QuillModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: 'pl',
      isolate: true,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    CreatorModule,
    LoadersWrapperComponent,
    CursorsWrapperComponent,
  ],
  providers: [
    {provide: APP_ID, useValue: 'serverApp'},
    // ZADANIE B5.4. Interceptor uwierzytelniania stoi PIERWSZY, czyli najbardziej
    // zewnętrznie: ponowienie po odnowieniu sesji przechodzi wtedy przez pozostałe
    // interceptory tak samo jak zwykłe żądanie, więc wołający dostaje odpowiedź w tym
    // samym kształcie co bez odnowienia.
    //
    // Ten wpis obsługuje serwisy z wstrzykiwacza GŁÓWNEGO — w tym koszyk i zamówienia,
    // dostarczane przez `SharedModule.forRoot()`. NIE obsługuje natomiast modułów konta
    // i koszyka, które importują własny `HttpClientModule` i mają przez to osobny łańcuch;
    // tam interceptor jest zarejestrowany drugi raz, u nich. Pierwsza wersja tego opisu
    // twierdziła, że tamten łańcuch jest martwy — nie jest, korzysta z niego
    // `ApiUserService`, i to twierdzenie kosztowało regresję.
    // AT-9296: pierwszy w lancuchu, zeby odcinek obejmowal takze prace pozostalych
    // interceptorow (konwersje case'u, naglowki) — inaczej ich koszt bylby niewidoczny.
    {provide: HTTP_INTERCEPTORS, useClass: PerfTimingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ConvertToCamelCase, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ConvertToSnakeCase, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true},
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ContentLanguageInterceptor,
      multi: true,
    },
    {provide: MatPaginatorIntl, useClass: MatPaginatorIntlPL},
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {subscriptSizing: 'dynamic'},
    },
    Title,
    Meta,
    PageService,
    GoogleService,
    Store,
    LocalStorageService,
    SessionStorageService,
    DisableNavWhenEdit,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
